import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AuraLayout,
  Button,
  Layout,
  Navbar,
  useBlockScroll,
} from "lola-framework-ui-test";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { APP_ROUTES, flavorClassName } from "../constants";
import { useClaimLink } from "../hooks/useClaimLink";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { isRouteNotFoundError, updateUserPhoneNumber } from "../services/api";
import { FlagCircle } from "../components/FlagCircle";
import { safeCapture } from "../utils/posthog";
import {
  DEFAULT_PHONE_VALUE,
  nextPhoneValue,
  parsePhoneInput,
  splitWithPlaceholder,
  toE164FromInput,
} from "../utils/phoneInput";
import "../styles/phonepage.style.css";

export const PhoneNumberPage = memo(() => {
  useClaimLink();
  useBlockScroll();
  const { t, i18n } = useTranslation();
  const { theme, setPhonePromptSkipped } = useStore();
  const { session, updateSession } = useSessionStore();
  const { redirectTo } = useRouteWithToken();

  const colorConfig = theme.whiteView;
  const { backgroundBtn, textColorBtn, title, buttonShowIcon, buttonSize } =
    colorConfig;

  // What the user sees in the field (includes the dial code). Starts with the
  // US dial code; clearing it and typing "+52" switches the country to Mexico.
  const [phone, setPhone] = useState(DEFAULT_PHONE_VALUE);
  const inputRef = useRef<HTMLInputElement>(null);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const parsed = useMemo(() => parsePhoneInput(phone), [phone]);
  const e164 = toE164FromInput(parsed);
  const isValid = e164 !== null;
  const showInvalid =
    touched && parsed.nationalDigits.length > 0 && !isValid;
  const { typed, rest } = splitWithPlaceholder(phone, parsed.country);

  useEffect(() => {
    safeCapture("ux.phone_number.screen.view");
    // Prefilled "+1": put the caret after it so the user types the number.
    const input = inputRef.current;
    if (input) {
      const end = input.value.length;
      input.setSelectionRange(end, end);
    }
  }, []);

  // Already on file (e.g. deep link / back nav) → nothing to capture.
  useEffect(() => {
    if (session.data?.hasPhoneNumber === true) {
      redirectTo(APP_ROUTES.SENDING_MONEY_PAGE, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.data?.hasPhoneNumber]);

  const handleContinue = useCallback(async () => {
    setTouched(true);
    if (!e164 || saving) return;

    setSaving(true);
    setSaveError(null);

    try {
      // SMS are only available in es/en; other languages fall back to the backend default.
      const lang = i18n.language?.slice(0, 2);
      const locale = lang === "es" || lang === "en" ? lang : undefined;
      await updateUserPhoneNumber(e164, locale);
      if (session.data) {
        updateSession({ ...session.data, hasPhoneNumber: true });
      }
      safeCapture("ux.phone_number.saved", { phone_country: parsed.country });
      redirectTo(APP_ROUTES.SENDING_MONEY_PAGE, { replace: true });
    } catch (err) {
      // Backend without POST /widget/users/me/phone deployed yet: don't trap
      // the user here. Continue without marking hasPhoneNumber so the number
      // is requested again once the endpoint exists.
      if (isRouteNotFoundError(err)) {
        console.warn(
          "[PhoneNumberPage] /widget/users/me/phone not available; continuing without saving"
        );
        safeCapture("ux.phone_number.save.error", {
          success: false,
          error_code: "PHONE_ENDPOINT_NOT_FOUND",
          phone_country: parsed.country,
        });
        redirectTo(APP_ROUTES.SENDING_MONEY_PAGE, { replace: true });
        return;
      }

      console.error("[PhoneNumberPage] Failed to save phone number:", err);
      safeCapture("ux.phone_number.save.error", {
        success: false,
        error_code: "PHONE_SAVE_FAILED",
        phone_country: parsed.country,
      });
      setSaveError(t("phone.saveError"));
    } finally {
      setSaving(false);
    }
  }, [
    e164,
    saving,
    parsed.country,
    session.data,
    redirectTo,
    updateSession,
    t,
    i18n.language,
  ]);

  const handleSkip = useCallback(() => {
    safeCapture("ui.phone_number.skip_button.click");
    setPhonePromptSkipped(true);
    redirectTo(APP_ROUTES.SENDING_MONEY_PAGE, { replace: true });
  }, [setPhonePromptSkipped, redirectTo]);

  return (
    <AuraLayout
      colorConfig={colorConfig}
      className={`${flavorClassName} scrollable phone-page`}
    >
      <Layout.Header>
        <Navbar
          title={t("phone.title")}
          align="center"
          color={title}
          noBackButton
        />
      </Layout.Header>

      <Layout.Content className="phone-page-content">
        <h3 className="foreground phone-page__heading">
          {t("phone.heading")}
        </h3>

        <section
          className={`phone-card ${showInvalid || saveError ? "phone-card--error" : ""}`.trim()}
        >
          <label className="phone-card__label" htmlFor="phone-number-input">
            <FlagCircle iso2={parsed.country.toLowerCase()} size={20} />
            <span className="muted-foreground">{t("phone.label")}</span>
          </label>
          <div className="phone-card__field">
            <div className="phone-card__ghost tnum" aria-hidden="true">
              <span className="foreground">{typed}</span>
              <span className="phone-card__rest">{rest}</span>
            </div>
            <input
              id="phone-number-input"
              className="phone-card__input tnum"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              autoFocus
              ref={inputRef}
              value={phone}
              onChange={(e) => {
                setPhone(nextPhoneValue(e.target.value));
                if (saveError) setSaveError(null);
              }}
              onBlur={() => {
                setTouched(true);
                // Never leave the field without a dial code.
                if (phone === "" || phone === "+") setPhone(DEFAULT_PHONE_VALUE);
              }}
              aria-label={t("phone.label")}
              aria-invalid={showInvalid}
            />
          </div>
        </section>
        {(showInvalid || saveError) && (
          <p className="phone-page__error" role="alert">
            {saveError ?? t("phone.invalid")}
          </p>
        )}

        <p className="muted-foreground phone-page__description">
          {t("phone.infoDescription")}
        </p>
      </Layout.Content>

      <Layout.Footer>
        <section className="footer-section">
          <button
            type="button"
            className="phone-page__skip"
            onClick={handleSkip}
            disabled={saving}
          >
            {t("phone.skip")}
          </button>
          <Button
            background={backgroundBtn}
            color={textColorBtn}
            showIcon={buttonShowIcon}
            size={buttonSize ?? "small"}
            disabled={!isValid || saving}
            onClick={handleContinue}
          >
            {saving ? t("phone.saving") : t("common.continue")}
          </Button>
        </section>
      </Layout.Footer>
    </AuraLayout>
  );
});
